import { FormEvent, useEffect, useRef, useState } from 'react';
import type { Scope } from '../scopes';
import { labelForScope, SCOPES } from '../scopes';
import type { TaskPriority } from '../types';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';

interface Props {
  open: boolean;
  defaultScope: Scope;
  onClose: () => void;
  onCreate: (input: {
    scope: Scope;
    title: string;
    priority: TaskPriority;
    dueDate?: string;
    note?: string;
    assigneeId?: string;
    private?: boolean;
  }) => Promise<void>;
}

export function NewTaskModal({ open, defaultScope, onClose, onCreate }: Props) {
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState<Scope>(defaultScope);
  const [priority, setPriority] = useState<TaskPriority>('low');
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { members } = useTeam();
  const { user } = useAuth();

  useEffect(() => {
    if (open) {
      setScope(defaultScope);
      setTitle('');
      setPriority('low');
      setDueDate('');
      setNote('');
      setIsPrivate(false);
      setAssigneeId(user?.userId ?? '');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, defaultScope, user]);

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await onCreate({
        scope,
        title: t,
        priority,
        dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
        note: note.trim() || undefined,
        assigneeId: assigneeId || undefined,
        private: isPrivate,
      });
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="new-task-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="New task"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form className="new-task-dialog" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header>
          <h3>New task</h3>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>
        <label>
          <span>Title</span>
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to happen?"
            required
          />
        </label>
        <div className="new-task-grid">
          <label>
            <span>Scope</span>
            <select value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
              {SCOPES.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          <label>
            <span>Due date</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
          {members.length > 1 && (
            <label>
              <span>Assignee</span>
              <select
                value={assigneeId || user?.userId || ''}
                disabled={isPrivate}
                onChange={(e) => setAssigneeId(e.target.value)}
                title={isPrivate ? 'Private tasks are assigned to you' : undefined}
              >
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <label className="new-task-private-row">
          <input
            type="checkbox"
            checked={isPrivate}
            onChange={(e) => {
              setIsPrivate(e.target.checked);
              if (e.target.checked) setAssigneeId(user?.userId ?? '');
            }}
          />
          <span>Private — only you will see this task</span>
        </label>
        <label>
          <span>Note (optional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Any context — the task will start in Pending if you add one"
            rows={3}
          />
        </label>
        <div className="new-task-footer">
          <span className="muted small">Adding to {labelForScope(scope)}</span>
          <button type="submit" className="primary" disabled={busy || !title.trim()}>
            {busy ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </form>
    </div>
  );
}
